import { type ListingAttributeV2 } from '@kijiji/generated/graphql-types'
import { type IconProps } from '@kijiji/icons/src/components/Icon'
import { CityIcon, MinStayIcon, SleepsIcon, VacationTypeIcon } from '@kijiji/icons/src/icons'
import AgreementTypeIcon from '@kijiji/icons/src/icons/AgreementType'
import AirConditioningIcon from '@kijiji/icons/src/icons/AirConditioning'
import AutosConvertibleIcon from '@kijiji/icons/src/icons/AutosConvertible'
import AutosCoupeIcon from '@kijiji/icons/src/icons/AutosCoupe'
import AutosCruiseControlIcon from '@kijiji/icons/src/icons/AutosCruiseControl'
import AutosDoorsIcon from '@kijiji/icons/src/icons/AutosDoors'
import AutosHatchbackIcon from '@kijiji/icons/src/icons/AutosHatchback'
import AutosOtherIcon from '@kijiji/icons/src/icons/AutosOther'
import AutosPickupIcon from '@kijiji/icons/src/icons/AutosPickup'
import AutosPriceIcon from '@kijiji/icons/src/icons/AutosPrice'
import AutosSedanIcon from '@kijiji/icons/src/icons/AutosSedan'
import AutosSuvIcon from '@kijiji/icons/src/icons/AutosSuv'
import AutosVanIcon from '@kijiji/icons/src/icons/AutosVan'
import AutosWagonIcon from '@kijiji/icons/src/icons/AutosWagon'
import BarrierFreeIcon from '@kijiji/icons/src/icons/BarrierFree'
import BathsIcon from '@kijiji/icons/src/icons/Baths'
import BedsIcon from '@kijiji/icons/src/icons/Beds'
import BirthdayIcon from '@kijiji/icons/src/icons/Birthday'
import CalenderIcon from '@kijiji/icons/src/icons/Calender'
import CashlessPaymentIcon from '@kijiji/icons/src/icons/CashlessPayment'
import CompanyIcon from '@kijiji/icons/src/icons/Company'
import ConditionCertifiedPreOwnedIcon from '@kijiji/icons/src/icons/ConditionCertifiedPreOwned'
import ConditionDamagedIcon from '@kijiji/icons/src/icons/ConditionDamaged'
import ConditionNewIcon from '@kijiji/icons/src/icons/ConditionNew'
import ConditionSalvageIcon from '@kijiji/icons/src/icons/ConditionSalvage'
import ConditionUsedIcon from '@kijiji/icons/src/icons/ConditionUsed'
import CurbsidePickupIcon from '@kijiji/icons/src/icons/CurbsidePickup'
import DeliveryIcon from '@kijiji/icons/src/icons/Delivery'
import DesktopBrandIcon from '@kijiji/icons/src/icons/DesktopBrand'
import ElectrfuelIcon from '@kijiji/icons/src/icons/Electrfuel'
import FemaleIcon from '@kijiji/icons/src/icons/Female'
import FrameSizeIcon from '@kijiji/icons/src/icons/FrameSize'
import FuelIcon from '@kijiji/icons/src/icons/Fuel'
import FuelHybridIcon from '@kijiji/icons/src/icons/FuelHybrid'
import JewelleryTypeIcon from '@kijiji/icons/src/icons/JewelleryType'
import JobOfferedByIcon from '@kijiji/icons/src/icons/JobOfferedBy'
import JobTypeIcon from '@kijiji/icons/src/icons/JobType'
import KmSimpleIcon from '@kijiji/icons/src/icons/KmSimple'
import LaptopBrandIcon from '@kijiji/icons/src/icons/LaptopBrand'
import MaleIcon from '@kijiji/icons/src/icons/Male'
import MoveInDateIcon from '@kijiji/icons/src/icons/MoveInDate'
import NoPetsIcon from '@kijiji/icons/src/icons/NoPets'
import OtherIcon from '@kijiji/icons/src/icons/Other'
import ParkingIcon from '@kijiji/icons/src/icons/Parking'
import PersonalOutdoorSpaceIcon from '@kijiji/icons/src/icons/PersonalOutdoorSpace'
import PetsIcon from '@kijiji/icons/src/icons/Pets'
import PhoneBrandIcon from '@kijiji/icons/src/icons/PhoneBrand'
import PhoneCarrierIcon from '@kijiji/icons/src/icons/PhoneCarrier'
import ScreenSizeIcon from '@kijiji/icons/src/icons/ScreenSize'
import ShippingIcon from '@kijiji/icons/src/icons/Shipping'
import SizeIcon from '@kijiji/icons/src/icons/Size'
import SmokingIcon from '@kijiji/icons/src/icons/Smoking'
import SquareFootageIcon from '@kijiji/icons/src/icons/SquareFootage'
import TabletBrandIcon from '@kijiji/icons/src/icons/TabletBrand'
import TractorIcon from '@kijiji/icons/src/icons/Tractor'
import TransmissionIcon from '@kijiji/icons/src/icons/Transmission'
import UnitTypeIcon from '@kijiji/icons/src/icons/UnitType'
import UnitTypeApartmentIcon from '@kijiji/icons/src/icons/UnitTypeApartment'
import UnitTypeBasementIcon from '@kijiji/icons/src/icons/UnitTypeBasement'
import UnitTypeCondoIcon from '@kijiji/icons/src/icons/UnitTypeCondo'
import UnitTypeDuplexIcon from '@kijiji/icons/src/icons/UnitTypeDuplex'
import UnitTypeHouseIcon from '@kijiji/icons/src/icons/UnitTypeHouse'
import UnitTypeTownhouseIcon from '@kijiji/icons/src/icons/UnitTypeTownhouse'
import VisualAidIcon from '@kijiji/icons/src/icons/VisualAid'
import { type TFunction } from 'i18next'
import { type FC } from 'react'

import {
  ATTRIBUTES,
  BOOLEAN_TEXT_VALUES,
  CAR_BODY_TYPE_VALUES,
  CAR_FUEL_VALUES,
  FULFILLMENT_VALUES,
  PAYMENT_VALUES,
  PET_FRIENDLY_VALUES,
  PET_GENDER_VALUES,
  UNIT_TYPE_VALUES,
  VEHICLE_TYPE_VALUES,
} from '@/features/attributes/constants/attributes'
import {
  type LabelOverrideProps,
  ATTRIBUTES_LABEL_OVERRIDE,
} from '@/features/attributes/constants/attributesLabelOverride'
import { formatAttributeLabel } from '@/features/attributes/utils/formatAttributeLabel'

export const ATTRIBUTE_ICON_DEFAULT = OtherIcon

export type AttributesConfigConcatenate = {
  /**
   * Canonical name of the second attribute to be concatenated
   * This attribute won't be returned in the list of attributes as an isolated one, but concatenated with the main one.
   * */
  canonicalName: string
  labelOverride: (
    t: TFunction,
    currAttribute: ListingAttributeV2,
    secondAttribute?: ListingAttributeV2
  ) => string
}

/**
 * This list will contain the attributes that should have dynamic (custom) content
 * All attributes that are not a part of this list should take the default attributes behaviour
 */
export type AttributeConfig = {
  [attrName: string]: {
    /**
     * Icon assigned for the attribute. It will only be returned if there is a specific icon defined for the attribute
     * */
    Icon?: FC<IconProps>
    /**
     * Icon for each value of an attribute.
     * It is only returned when there are dynamic icons per attr value.
     * */
    valueIcon?: { [canonicalValue: string]: FC<IconProps> }
    /**
     * Value that should be returned when the label to be displayed should not follow
     * the dynamic pattern established for all other fields.
     */
    labelOverride?: LabelOverrideProps
    /**
     * A few attributes have more than one value
     * The "labelOverride" will return all of them joined by default,
     * this field will indicate when/how to treat each of the values individually so they can be separated to be displayed
     */
    multipleLabelOverride?: (
      attribute: { canonicalName: string; canonicalValue: string },
      t: TFunction
    ) => string
    /**
     * Some attributes will need their labels to be a concatenation of 2 different attributes
     * This happens the most with range attributes, or dates (i.e. start - end)
     */
    concatenateMultipleAttributes?: AttributesConfigConcatenate
  }
}

export const ATTRIBUTES_CONFIG: AttributeConfig = {
  [ATTRIBUTES.AGREEMENT_TYPE]: { Icon: AgreementTypeIcon },
  [ATTRIBUTES.AIR_CONDITIONING]: { Icon: AirConditioningIcon },
  [ATTRIBUTES.AREA_IN_FEET]: {
    Icon: SquareFootageIcon,
    labelOverride: (attribute, t) => {
      const value = ATTRIBUTES_LABEL_OVERRIDE.FORMAT_NUMBER(attribute, t)
      return (
        ATTRIBUTES_LABEL_OVERRIDE.NOT_AVAILABLE({ ...attribute, values: [value] }, t) ??
        t('listing:attribute.label_override.sqft', { value })
      )
    },
  },
  [ATTRIBUTES.AVAILABILITY_END_DATE]: { Icon: CalenderIcon },
  [ATTRIBUTES.AVAILABILITY_START_DATE]: {
    Icon: CalenderIcon,
    concatenateMultipleAttributes: {
      canonicalName: ATTRIBUTES.AVAILABILITY_END_DATE,
      labelOverride: (t, attribute1, attribute2) => {
        const label1 = `${t('listing:attribute.range.from')} ${formatAttributeLabel(attribute1)}`
        if (!attribute2) return label1

        const label2 = formatAttributeLabel(attribute2)
        return `${label1} ${t('listing:attribute.range.to')} ${label2}`
      },
    },
  },
  [ATTRIBUTES.BALCONY]: { Icon: PersonalOutdoorSpaceIcon },
  [ATTRIBUTES.BARRIER_FREE]: { Icon: BarrierFreeIcon },
  [ATTRIBUTES.BATHROOMS]: { Icon: BathsIcon },
  [ATTRIBUTES.BEDROOMS]: { Icon: BedsIcon },
  [ATTRIBUTES.BIRTH_DATE]: { Icon: BirthdayIcon },
  [ATTRIBUTES.BRAND_DESKTOP]: { Icon: DesktopBrandIcon },
  [ATTRIBUTES.BRAND_LAPTOP]: { Icon: LaptopBrandIcon },
  [ATTRIBUTES.BRAND_PHONE]: { Icon: PhoneBrandIcon },
  [ATTRIBUTES.BRAND_TABLET]: { Icon: TabletBrandIcon },
  [ATTRIBUTES.BREED]: { Icon: PetsIcon },
  [ATTRIBUTES.CAR_BODY_TYPE]: {
    Icon: AutosOtherIcon,
    valueIcon: {
      [CAR_BODY_TYPE_VALUES.CONVERTIBLE]: AutosConvertibleIcon,
      [CAR_BODY_TYPE_VALUES.COUPE]: AutosCoupeIcon,
      [CAR_BODY_TYPE_VALUES.HATCHBACK]: AutosHatchbackIcon,
      [CAR_BODY_TYPE_VALUES.PICKUP_TRUCK]: AutosPickupIcon,
      [CAR_BODY_TYPE_VALUES.SEDAN]: AutosSedanIcon,
      [CAR_BODY_TYPE_VALUES.SUV_CROSSOVER]: AutosSuvIcon,
      [CAR_BODY_TYPE_VALUES.VAN]: AutosVanIcon,
      [CAR_BODY_TYPE_VALUES.WAGON]: AutosWagonIcon,
    },
  },
  [ATTRIBUTES.CAR_FUEL_TYPE]: {
    labelOverride: ({ values, canonicalValues }) => {
      const value = values[0]
      if (!canonicalValues || canonicalValues?.[0] === CAR_FUEL_VALUES.OTHER) return ''

      return value ?? ''
    },
    Icon: FuelIcon,
    valueIcon: {
      [CAR_FUEL_VALUES.HYBRID]: FuelHybridIcon,
      [CAR_FUEL_VALUES.ELECTRIC]: ElectrfuelIcon,
    },
  },
  [ATTRIBUTES.CAR_MILEAGE]: { Icon: KmSimpleIcon },
  [ATTRIBUTES.CAR_TRANSMISSION]: { Icon: TransmissionIcon },
  [ATTRIBUTES.CASHLESS_PAYMENT]: { valueIcon: { [BOOLEAN_TEXT_VALUES.YES]: CashlessPaymentIcon } },
  [ATTRIBUTES.CASH_ACCEPTED]: { valueIcon: { [BOOLEAN_TEXT_VALUES.YES]: AutosPriceIcon } },
  [ATTRIBUTES.CITY]: { Icon: CityIcon },
  [ATTRIBUTES.COMPANY]: { Icon: CompanyIcon },
  [ATTRIBUTES.CPO]: { Icon: ConditionCertifiedPreOwnedIcon },
  [ATTRIBUTES.CRUISE_CONTROL]: { Icon: AutosCruiseControlIcon },
  [ATTRIBUTES.CURBSIDE]: { valueIcon: { [BOOLEAN_TEXT_VALUES.YES]: CurbsidePickupIcon } },
  [ATTRIBUTES.DATE_AVAILABLE]: { Icon: MoveInDateIcon },
  [ATTRIBUTES.DROP_OFF]: { valueIcon: { [BOOLEAN_TEXT_VALUES.YES]: DeliveryIcon } },
  [ATTRIBUTES.FRAME_SIZE]: { Icon: FrameSizeIcon },
  [ATTRIBUTES.FULFILLMENT]: {
    valueIcon: {
      [FULFILLMENT_VALUES.CURBSIDE]: CurbsidePickupIcon,
      [FULFILLMENT_VALUES.DELIVERY]: DeliveryIcon,
      [FULFILLMENT_VALUES.SHIPPING]: ShippingIcon,
    },
  },
  [ATTRIBUTES.GENDER]: {
    valueIcon: { [PET_GENDER_VALUES.FEMALE]: FemaleIcon, [PET_GENDER_VALUES.MALE]: MaleIcon },
  },
  [ATTRIBUTES.HEAVY_EQUIPMENT_TYPE]: { Icon: TractorIcon },
  [ATTRIBUTES.JEWELLERY_TYPE]: { Icon: JewelleryTypeIcon },
  [ATTRIBUTES.JOB_OFFERED_BY]: { Icon: JobOfferedByIcon },
  [ATTRIBUTES.JOB_TYPE]: { Icon: JobTypeIcon },
  [ATTRIBUTES.MIN_NIGHTS]: { Icon: MinStayIcon },
  [ATTRIBUTES.NUM_DOORS]: { Icon: AutosDoorsIcon },
  [ATTRIBUTES.PARKING]: { Icon: ParkingIcon },
  [ATTRIBUTES.PAYMENT]: {
    valueIcon: {
      [PAYMENT_VALUES.CASHLESS]: CashlessPaymentIcon,
      [PAYMENT_VALUES.CASH_ACCEPTED]: AutosPriceIcon,
    },
  },
  [ATTRIBUTES.PET_FRIENDLY]: {
    Icon: PetsIcon,
    valueIcon: { [PET_FRIENDLY_VALUES.TRUE]: PetsIcon, [PET_FRIENDLY_VALUES.FALSE]: NoPetsIcon },
  },
  [ATTRIBUTES.PHONE_CARRIER]: { Icon: PhoneCarrierIcon },
  [ATTRIBUTES.SCREEN_SIZE_LAPTOP]: { Icon: ScreenSizeIcon },
  [ATTRIBUTES.SHIPPING]: { valueIcon: { [BOOLEAN_TEXT_VALUES.YES]: ShippingIcon } },
  [ATTRIBUTES.SIZE]: { Icon: SizeIcon },
  [ATTRIBUTES.SLEEPS]: { Icon: SleepsIcon },
  [ATTRIBUTES.SMOKING_PERMITTED]: { Icon: SmokingIcon },
  [ATTRIBUTES.UNIT_TYPE]: {
    Icon: UnitTypeIcon,
    valueIcon: {
      [UNIT_TYPE_VALUES.CONDO]: UnitTypeCondoIcon,
      [UNIT_TYPE_VALUES.APARTMENT]: UnitTypeApartmentIcon,
      [UNIT_TYPE_VALUES.TOWNHOUSE]: UnitTypeTownhouseIcon,
      [UNIT_TYPE_VALUES.DUPLEX]: UnitTypeDuplexIcon,
      [UNIT_TYPE_VALUES.BASEMENT_APARTMENT]: UnitTypeBasementIcon,
      [UNIT_TYPE_VALUES.HOUSE]: UnitTypeHouseIcon,
    },
  },
  [ATTRIBUTES.VACATION_TYPE]: { Icon: VacationTypeIcon },
  [ATTRIBUTES.VEHICLE_TYPE]: {
    valueIcon: {
      [VEHICLE_TYPE_VALUES.DAMAGED]: ConditionDamagedIcon,
      [VEHICLE_TYPE_VALUES.NEW]: ConditionNewIcon,
      [VEHICLE_TYPE_VALUES.SALVAGE]: ConditionSalvageIcon,
      [VEHICLE_TYPE_VALUES.USED]: ConditionUsedIcon,
    },
  },
  [ATTRIBUTES.VISUAL_AIDS]: { Icon: VisualAidIcon },
}
